import { cloneRng } from './rng';
import type { Drone, Drop, Effect, Enemy, PendingSpawn, Projectile, Slot, World } from './types';

/**
 * A restore point for the world.
 *
 * Taken when a build phase opens, so "restart this wave" puts everything back
 * exactly as it was before the player committed — the structures they'd built,
 * the juice they hadn't spent, the Integrity they still had.
 *
 * Structural clone rather than JSON: entities hold references to their content
 * definitions, and those must stay identity-equal or every `def ===` comparison
 * in the sim quietly stops matching.
 */
export interface WorldSnapshot {
  tick: number;
  time: number;
  rng: { s: number };
  planetRot: number;
  ringRot: number;
  ringVel: number;
  ringR: number;
  slots: Slot[];
  enemies: Enemy[];
  projectiles: Projectile[];
  effects: Effect[];
  drones: Drone[];
  drops: Drop[];
  juice: number;
  coreHp: number;
  coreMax: number;
  coreDamageTaken: number;
  upgrades: Record<string, number>;
  waveIndex: number;
  waveState: World['waveState'];
  waveTimer: number;
  pending: PendingSpawn[];
  ultCd: number;
  stats: World['stats'];
}

export function snapshotWorld(world: World): WorldSnapshot {
  return {
    tick: world.tick,
    time: world.time,
    rng: cloneRng(world.rng),
    planetRot: world.planetRot,
    ringRot: world.ringRot,
    ringVel: world.ringVel,
    ringR: world.ringR,
    slots: world.slots.map((s) => ({ ...s, structure: s.structure ? { ...s.structure } : null })),
    enemies: world.enemies.map((e) => ({ ...e })),
    projectiles: world.projectiles.map((p) => ({ ...p })),
    effects: world.effects.map((f) => ({ ...f, hit: new Set(f.hit) })),
    drones: world.drones.map((d) => ({ ...d })),
    drops: world.drops.map((d) => ({ ...d })),
    juice: world.juice,
    coreHp: world.coreHp,
    coreMax: world.coreMax,
    coreDamageTaken: world.coreDamageTaken,
    upgrades: { ...world.upgrades },
    waveIndex: world.waveIndex,
    waveState: world.waveState,
    waveTimer: world.waveTimer,
    pending: world.pending.map((p) => ({ ...p })),
    ultCd: world.ultCd,
    stats: { ...world.stats },
  };
}

/**
 * Restores in place. The pools are written element by element rather than
 * replaced, because the renderer holds a sprite per pool index and swapping the
 * arrays would leave it drawing a stale set.
 */
export function restoreWorld(world: World, snap: WorldSnapshot): void {
  world.tick = snap.tick;
  world.time = snap.time;
  world.rng.s = snap.rng.s;
  world.planetRot = snap.planetRot;
  world.ringRot = snap.ringRot;
  world.ringVel = snap.ringVel;
  world.ringR = snap.ringR;

  world.slots.length = 0;
  for (const s of snap.slots) {
    world.slots.push({ ...s, structure: s.structure ? { ...s.structure } : null });
  }

  copyPool(world.enemies, snap.enemies);
  copyPool(world.projectiles, snap.projectiles);
  copyPool(world.drones, snap.drones);
  copyPool(world.drops, snap.drops);

  for (let i = 0; i < world.effects.length; i++) {
    const from = snap.effects[i];
    if (!from) continue;
    Object.assign(world.effects[i]!, from, { hit: world.effects[i]!.hit });
    world.effects[i]!.hit.clear();
    for (const id of from.hit) world.effects[i]!.hit.add(id);
  }

  world.juice = snap.juice;
  world.coreHp = snap.coreHp;
  world.coreMax = snap.coreMax;
  world.coreDamageTaken = snap.coreDamageTaken;
  world.upgrades = { ...snap.upgrades };
  world.waveIndex = snap.waveIndex;
  world.waveState = snap.waveState;
  world.waveTimer = snap.waveTimer;
  world.pending = snap.pending.map((p) => ({ ...p }));
  world.ultCd = snap.ultCd;
  world.stats = { ...snap.stats };
  world.events.length = 0;
}

function copyPool<T extends object>(live: T[], saved: T[]): void {
  for (let i = 0; i < live.length; i++) {
    const from = saved[i];
    if (from) Object.assign(live[i]!, from);
  }
  // A restore never grows a pool, but it can shrink one (drones grow on demand).
  for (let i = saved.length; i < live.length; i++) {
    (live[i] as unknown as { alive: boolean }).alive = false;
  }
}
